<template>
  <div class="cms-faq-category-form">
    <cms-form-header
      :title="isEditing ? $t('cms.faq.editCategory') : $t('cms.faq.createCategory')"
      :disabled="isSaving || isLoadingCategory"
      @back="goBack"
      @save-draft="saveDraft"
      @save-publish="saveAndPublish"
    />

    <cms-form-layout>
      <form @submit.prevent="handleSubmit">
        <cms-form-section :title="$t('cms.faq.generalInfo')">
          <cms-translation-tabs :languages="languages" v-model="currentLanguage">
            <template #default="{ locale }">
              <div class="cms-form-grid">
                <cms-input
                  v-model="getTranslation(locale).name"
                  :label="`${$t('cms.faq.categoryName')} (${locale.toUpperCase()})`"
                  :full-width="true"
                />
                <cms-input
                  v-model="getTranslation(locale).slug"
                  :label="`${$t('cms.faq.categorySlug')} (${locale.toUpperCase()})`"
                  :full-width="true"
                />
                <cms-textarea
                  v-model="getTranslation(locale).description"
                  :label="`${$t('cms.faq.categoryDescription')} (${locale.toUpperCase()})`"
                  :full-width="true"
                  :rows="3"
                />
              </div>
            </template>
          </cms-translation-tabs>
        </cms-form-section>
      </form>
    </cms-form-layout>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toastification';
import api from '@/api';
import { useAuthStore } from '@/stores/auth';
import { useAppLanguages } from '@/composables/useAppLanguages';
import {
  CmsFormHeader,
  CmsFormLayout,
  CmsFormSection,
  CmsInput,
  CmsTextarea,
  CmsTranslationTabs,
} from '@/components/cms';
import { normalizeResource, extractRelationship } from '@/composables/useCmsActivityData';

interface CategoryTranslation {
  locale: string;
  name: string;
  slug: string;
  description: string;
}

interface CategoryForm {
  order: number;
  is_public: boolean;
  translations: CategoryTranslation[];
}

export default defineComponent({
  name: 'TheCmsFaqCategoryForm',
  components: {
    CmsFormHeader,
    CmsFormLayout,
    CmsFormSection,
    CmsInput,
    CmsTextarea,
    CmsTranslationTabs,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { t } = useI18n();
    const toast = useToast();
    const authStore = useAuthStore();

    const agencySlug = computed(() => authStore.agency?.slug ?? null);
    const categoryId = ref(route.params.id ? Number(route.params.id) : null);
    const isEditing = computed(() => categoryId.value !== null);
    const isSaving = ref(false);
    const isLoadingCategory = ref(false);

    const { languages, locales, resolveStoredLocale } = useAppLanguages();
    const currentLanguage = ref(resolveStoredLocale(localStorage.getItem('language')));

    const createEmptyTranslation = (locale: string): CategoryTranslation => ({
      locale,
      name: '',
      slug: '',
      description: '',
    });

    const form = reactive<CategoryForm>({
      order: 0,
      is_public: false,
      translations: locales.value.map((locale) => createEmptyTranslation(locale)),
    });

    const getTranslation = (locale: string): CategoryTranslation => {
      let translation = form.translations.find((t) => t.locale === locale);
      if (!translation) {
        translation = createEmptyTranslation(locale);
        form.translations.push(translation);
      }
      return translation;
    };

    const loadCategory = async () => {
      if (!agencySlug.value || !categoryId.value) {
        return;
      }
      isLoadingCategory.value = true;
      try {
        const response = await api.faqCategoryService.getCmsFaqCategory(agencySlug.value, categoryId.value);
        const category = normalizeResource(response) || response;

        form.order = category.order || 0;
        form.is_public = category.is_public || false;

        const translations = normalizeResource(extractRelationship(response, 'translations')) || [];
        form.translations = locales.value.map((locale) => {
          const translation = translations.find((t: any) => t.locale === locale) || {};
          return {
            locale,
            name: translation.name || '',
            slug: translation.slug || '',
            description: translation.description || '',
          };
        });
      } catch (error) {
        console.error('Failed to load category', error);
        toast.error(t('general.errorMessage'));
      } finally {
        isLoadingCategory.value = false;
      }
    };

    const buildPayload = () => {
      return {
        order: form.order,
        is_public: form.is_public,
        translations: form.translations
          .filter((t) => t.name.trim())
          .map((t) => ({
            locale: t.locale,
            name: t.name.trim(),
            slug: t.slug.trim() || t.name.trim().toLowerCase().replace(/\s+/g, '-'),
            description: t.description.trim() || null,
          })),
      };
    };

    const saveDraft = async () => {
      if (!agencySlug.value) return;

      form.is_public = false;
      await persistCategory(false);
    };

    const saveAndPublish = async () => {
      if (!agencySlug.value) return;

      form.is_public = true;
      await persistCategory(true);
    };

    const persistCategory = async (publish = false) => {
      if (!agencySlug.value) return;

      isSaving.value = true;
      try {
        const payload = buildPayload();

        if (isEditing.value && categoryId.value) {
          await api.faqCategoryService.updateCmsFaqCategory(agencySlug.value, categoryId.value, payload);
          if (publish) {
            await api.faqCategoryService.publishCmsFaqCategory(agencySlug.value, categoryId.value, { is_public: true });
          }
          toast.success(publish ? t('cms.faq.publishSuccess') : t('general.shared.savedChanges'));
        } else {
          const response = await api.faqCategoryService.createCmsFaqCategory(agencySlug.value, payload);
          const newId = response?.id || response?.data?.id || response?.data?.data?.id;
          if (newId) {
            categoryId.value = newId;
            if (publish) {
              await api.faqCategoryService.publishCmsFaqCategory(agencySlug.value, newId, { is_public: true });
            }
            await router.replace({ name: 'cms.help-center.edit-category', params: { id: newId } });
          }
          toast.success(publish ? t('cms.faq.publishSuccess') : t('general.shared.savedChanges'));
        }
      } catch (error: any) {
        console.error('Failed to save category', error);
        const message = error?.response?.data?.message || t('general.errorMessage');
        toast.error(message);
      } finally {
        isSaving.value = false;
      }
    };

    const handleSubmit = () => {
      saveAndPublish();
    };

    const goBack = () => {
      router.push({ name: 'cms.help-center' });
    };

    watch(
      () => route.params.id,
      async (newId) => {
        if (newId) {
          categoryId.value = Number(newId);
          await loadCategory();
        }
      },
      { immediate: true },
    );

    return {
      isEditing,
      isSaving,
      isLoadingCategory,
      form,
      languages,
      currentLanguage,
      getTranslation,
      handleSubmit,
      saveDraft,
      saveAndPublish,
      goBack,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/components/cms/cms-styles.scss';

.cms-faq-category-form {
  color: $cms-text-primary;
}
</style>

